import { IUser } from '@api/models/user.model';
import { CrossIcon, ReloadIcon } from '@icon/index';
import { DatePicker } from '@library/datepicker';
import { FormHelperText, Input, Label } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { useMutation } from '@tanstack/react-query';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { generate } from 'generate-password-browser';
import http from 'httpConfig/http';
import CustomSwitch from 'modules/shared/components/FormFields/CustomSwitch';
import RequiredMark from 'modules/shared/components/FormFields/RequiredMark';
import { useState } from 'react';
import { ColorResult } from 'react-color';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { roleOption, statusOption, themeOption, timezoneOption } from '../../helpers/user.utils';
import useUniqueMutation from '../../hooks/useUniqueMutation';

import { Button, Card, Chip, Drawer, LinearProgress } from '@hyperflake/react-ui-library';
import ColorPicker from 'modules/shared/components/ColorPicker/ColorPicker';
import { getResellerIdFromRedux } from 'modules/shared/helpers/shared.utils';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import useUserEditForm from 'modules/users/hooks/useUserEditForm';
import PhoneDetails from '../PhoneDetails/PhoneDetails';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { PermissionEnum } from '@api/enum';
import UserUpdateDp from '../UserUpdateDp/UserUpdateDp';
import useUserData from 'modules/users/hooks/useUserData';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import ExternalProjectList from '../ExternalProjectList/ExternalProjectList';
import ProjectAccessDrawerForm from '../ProjectAccessDrawerForm/ProjectAccessDrawerForm';
import ProjectAccessListForm from '../ProjectAccessListForm/ProjectAccessListForm';
import { IClient } from '@api/models';

interface IUserEditFromProps {}

const UserEditFrom = (props: IUserEditFromProps) => {
    const { clientId, userId } = useParams();

    const [tagHolder, setTagHolder] = useState('');

    const { uniqueEmailMutation, uniqueUsernameMutation } = useUniqueMutation();

    const editUserForm = useUserEditForm(clientId, userId);

    const hasPhoneNumber = (data: any) =>
        data.phone && data.phone.number && data.phone.dialCode && data.phone.countryCode;

    const handleSubmit = async (values: any) => {
        try {
            const data = await editUserForm.submit(values);
            toast.success('User Updated Successfully');
        } catch (e: any) {
            console.log(e);
            toast.error('Oops something went wrong.');
        }
    };

    const userData = useUserData(clientId, userId);

    const handleUserDpUpdate = (user: IUser) => {
        userData.setData(user);
    };

    const user = userData.data;

    const updatePasswordMutation = useMutation(async (password: string) => {
        const res = await http.put(
            `${
                import.meta.env.VITE_APP_API_URL
            }/${getResellerIdFromRedux()}/clients/${clientId}/users/${userId}/password`,
            {
                password,
            }
        );
        return res;
    });

    const handleUpdatePassword = async (newPassword: string): Promise<boolean> => {
        try {
            await updatePasswordMutation.mutateAsync(newPassword);
            toast.success('Password Updated');
            return false;
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong while updating password');
            return true;
        }
    };

    const generatePassword = () => {
        const password = generate({
            length: 8,
            uppercase: false,
            numbers: true,
        });
        return password;
    };

    const { hasPermission } = useAuthPermission();

    const handleCopyDetails = () => {
        let text = `Username: ${user.username} \n`;
        text += `Password: ${user.password} \n`;
        const client = user.client as IClient;

        text += `Sign-in url: ${client.domain?.trim() || import.meta.env.VITE_APP_DEFAULT_URL}`;

        navigator.clipboard.writeText(text);
        toast.success(`Credentials Copied of ${user.name}`);
    };

    return (
        <PageTemplate>
            <PageHeading>Edit User</PageHeading>
            {editUserForm.userLoading ? (
                <LinearProgress color="primary" />
            ) : (
                <div>
                    <Card className="mt-4">
                        <Card.Header>
                            <div className="flex justify-between items-center">
                                <div>Profile Pictures</div>
                                <Button size="xs" onClick={() => handleCopyDetails()}>
                                    Copy Credentials
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <UserUpdateDp user={user} clientId={clientId} onDpUrlUpdate={handleUserDpUpdate} />
                        </Card.Body>
                    </Card>

                    <Formik
                        initialValues={editUserForm.initialValues}
                        enableReinitialize
                        validationSchema={editUserForm.schema}
                        validateOnChange={false}
                        onSubmit={handleSubmit}
                    >
                        {({ setFieldValue, values, isValidating, setFieldError, setFieldTouched }) => (
                            <Form>
                                <fieldset disabled={!hasPermission(PermissionEnum.USER_UPDATE)}>
                                    <Card className="mt-4 ">
                                        <Card.Header>Basic Details</Card.Header>
                                        <Card.Body>
                                            <div>
                                                {/* 🔥 Responsive basic details: 1 col mobile, 2 cols md+ */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 mt-4 gap-4">
                                                    {/* name */}
                                                    <div>
                                                        <Label>
                                                            Name <RequiredMark />
                                                        </Label>
                                                        <FormikInput type="text" name="name" />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="name" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* username */}
                                                    <div>
                                                        <Label>
                                                            Username <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2 items-center">
                                                            <FormikInput type="text" name="username" />
                                                            {uniqueUsernameMutation.isLoading && (
                                                                <CircularProgress size={14} />
                                                            )}
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="username" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* email */}
                                                    <div>
                                                        <Label>
                                                            Email <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2 items-center">
                                                            <FormikInput type="email" name="email" />
                                                            {uniqueEmailMutation.isLoading && (
                                                                <CircularProgress size={14} />
                                                            )}
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="email" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* password */}
                                                    <div>
                                                        <Label>
                                                            Password <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2 items-center">
                                                            <FormikInput
                                                                type="text"
                                                                name="password"
                                                                disabled={updatePasswordMutation.isLoading}
                                                            />
                                                            <div
                                                                className="cursor-pointer hover:rotate-12"
                                                                onClick={async () => {
                                                                    if (!hasPermission(PermissionEnum.USER_UPDATE))
                                                                        return;
                                                                    const generatedPassword = generatePassword();
                                                                    await setFieldTouched('password');
                                                                    await setFieldValue('password', generatedPassword);
                                                                    const isError = await handleUpdatePassword(
                                                                        generatedPassword
                                                                    );
                                                                    if (isError) {
                                                                        // setFieldError('password', '');
                                                                    }
                                                                }}
                                                            >
                                                                <ReloadIcon />
                                                            </div>
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="password" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* role */}
                                                    <div>
                                                        <Label>
                                                            Role <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect name="role" options={roleOption} />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="role" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* designation */}
                                                    <div>
                                                        <Label>Designation</Label>
                                                        <FormikInput type="text" name="designation" />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="designation" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* dob */}
                                                    <div>
                                                        <Label>Date of birth</Label>
                                                        <DatePicker
                                                            className="flex items-center w-full font-medium bg-transparent border border-grayscale-300 placeholder:text-grayscale-400 focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10 text-sm px-4 h-10 rounded-md"
                                                            onChange={(val) => setFieldValue('dob', val)}
                                                            value={values?.dob || undefined}
                                                            maxDate={new Date()}
                                                        />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="dob" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* mobile number */}
                                                    <div>
                                                        <Label>Phone Number</Label>
                                                        <PhoneDetails />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="phone.number" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* timezone */}
                                                    <div>
                                                        <Label>
                                                            Timezone <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="preferences.timezone"
                                                                options={timezoneOption}
                                                                showSearch
                                                            />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="preferences.timezone" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* status */}
                                                    <div>
                                                        <Label>
                                                            Status <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect name="status" options={statusOption} />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="status" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* primary color */}
                                                    <div>
                                                        <Label>Primary Color</Label>
                                                        <ColorPicker
                                                            value={values.preferences?.primaryColor}
                                                            setColor={(event: ColorResult) => {
                                                                setFieldValue(
                                                                    'preferences.primaryColor',
                                                                    event.hex.toUpperCase()
                                                                );
                                                            }}
                                                        />
                                                    </div>

                                                    {/* theme */}
                                                    <div>
                                                        <Label>
                                                            Theme <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="preferences.theme"
                                                                options={themeOption}
                                                            />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="preferences.theme" />
                                                        </FormHelperText>
                                                    </div>
                                                </div>

                                                {/* 🔥 Switches: 1 col mobile, 2 cols md+ */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                    <div>
                                                        <CustomSwitch
                                                            name="preferences.isEmailNotificationEnabled"
                                                            label="Email Notification"
                                                        />
                                                        <FormHelperText variant={'error'} />
                                                    </div>
                                                    <div>
                                                        <CustomSwitch name="preferences.show4k" label="Show 4K" />
                                                        <FormHelperText variant={'error'} />
                                                    </div>
                                                    <div>
                                                        <CustomSwitch
                                                            name="preferences.showZoomed"
                                                            label="Show Zoomed"
                                                        />
                                                        <FormHelperText variant={'error'} />
                                                    </div>
                                                    <div>
                                                        <CustomSwitch
                                                            name="preferences.showTimestamp"
                                                            label="Show Timestamp"
                                                        />
                                                        <FormHelperText variant={'error'} />
                                                    </div>
                                                </div>

                                                {/* Tags */}
                                                <div className="mt-6">
                                                    <Label>Tags</Label>
                                                    <FieldArray
                                                        name="tags"
                                                        render={(arrayHelpers) => {
                                                            return (
                                                                <div>
                                                                    <Input
                                                                        className="form-control"
                                                                        type="text"
                                                                        placeholder="Add tag"
                                                                        value={tagHolder}
                                                                        onKeyDown={(
                                                                            event: React.KeyboardEvent<HTMLElement>
                                                                        ) => {
                                                                            if (event.key === 'Enter') {
                                                                                const trimmedValue = tagHolder.trim();
                                                                                if (trimmedValue !== '') {
                                                                                    arrayHelpers.push(trimmedValue);
                                                                                    setTagHolder('');
                                                                                    event.preventDefault();
                                                                                }
                                                                            }
                                                                        }}
                                                                        onInput={(event: any) => {
                                                                            setTagHolder(event.target.value);
                                                                        }}
                                                                        onBlur={() => {
                                                                            setTagHolder('');
                                                                        }}
                                                                    />
                                                                    <div className="flex flex-wrap gap-2 mt-4">
                                                                        {values.tags.map((_: any, index: any) => (
                                                                            <Chip
                                                                                key={index}
                                                                                label={values.tags[index]}
                                                                                variant={'default'}
                                                                                onClick={() => {
                                                                                    arrayHelpers.remove(index);
                                                                                }}
                                                                                className="cursor-pointer"
                                                                                icon={
                                                                                    <CrossIcon width={8} height={8} />
                                                                                }
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }}
                                                    />

                                                    <FormHelperText variant={'error'}>
                                                        <ErrorMessage name="timezone" />
                                                    </FormHelperText>
                                                </div>
                                            </div>

                                            <Button
                                                disabled={editUserForm.isSubmitting || isValidating}
                                                type="submit"
                                                size={'lg'}
                                                className="w-full sm:w-auto"
                                            >
                                                {editUserForm.isSubmitting ? (
                                                    <CircularProgress size={20} color={'inherit'} />
                                                ) : isValidating ? (
                                                    'Validating'
                                                ) : (
                                                    'Save'
                                                )}
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </fieldset>
                            </Form>
                        )}
                    </Formik>

                    <Card className="mt-4">
                        <Card.Header>Project Access</Card.Header>
                        <Card.Body>
                            <ProjectAccessListForm />
                        </Card.Body>
                    </Card>

                    <Card className="mt-4">
                        <Card.Header>External Project</Card.Header>
                        <Card.Body>
                            <ExternalProjectList user={user} />
                        </Card.Body>
                    </Card>
                </div>
            )}
        </PageTemplate>
    );
};

export default UserEditFrom;
